import { Box, Tooltip } from "@mui/material";
import React, { useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  BudgetItemTypeEnum,
  CategoryTotals,
} from "../../../../types/budget/budget";
import { BudgetIcons } from "../../../../assets/styled-icons/budget";
import { BudgetItemIcon } from "../components/BudgetItemIcon";
import { currencyFormatter } from "../../../../utils/currencyFormatter";
import { TabActive } from "../../../../types/project.type";
import MoveCategorySelect from "../MoveCategorySelect";

export type CategoryAccordionSummaryProps = {
  type: keyof typeof BudgetItemTypeEnum;
  index: number;
  totals: CategoryTotals;
  comparison: CategoryTotals;
  tabActive: number;
  baseValueExchangeCurrency: string;
  isBudgetDisabled: boolean;
  onMoveBillingModel: (type: keyof typeof BudgetItemTypeEnum) => void;
  onTransferSupplier: (type: keyof typeof BudgetItemTypeEnum) => void;
  onRemoveItem: (type: keyof typeof BudgetItemTypeEnum) => void;
  /**
   * Linha secundária sob o nome da categoria.
   *
   * Na visão "Por Fornecedor" não existe: o fornecedor é o bloco que contém a
   * categoria. Na visão "Por Agrupamento" o que contém é o agrupamento, então a
   * linha precisa dizer de quem ela é.
   */
  subtitle?: React.ReactNode;
  /** Espaço antes do número — a alça de arrasto da visão agrupada. */
  leadingSlot?: React.ReactNode;
  /**
   * Esconde o comparativo de savings.
   *
   * A árvore de totais agrega por fornecedor e não conhece agrupamento, então
   * na visão agrupada os totais chegam recalculados por `sumServiceTotals` e
   * não há comparação equivalente para exibir — melhor nada do que um savings
   * que sempre daria zero.
   */
  hideSavings?: boolean;
};

const CategoryAccordionSummary: React.FC<CategoryAccordionSummaryProps> = ({
  type,
  index,
  totals,
  comparison,
  tabActive,
  baseValueExchangeCurrency,
  isBudgetDisabled,
  onMoveBillingModel,
  onTransferSupplier,
  onRemoveItem,
  subtitle,
  leadingSlot,
  hideSavings,
}) => {
  const totalsSummary = useMemo(
    () => [
      { title: "T. Tarifas", value: totals.tariffTotals },
      { title: "T. Taxas", value: totals.serviceTaxTotals },
      { title: "T. Receita", value: totals.revenueTotals },
      { title: "T. Imposto", value: totals.taxes },
      { title: "T. Geral", value: totals.totals },
    ],
    [totals],
  );

  const savingsSummary = useMemo(
    () => [
      {
        title: "T. Tarifas",
        value:
          totals.tariffTotals.totalInBRL - comparison.tariffTotals.totalInBRL,
      },
      {
        title: "T. Taxas",
        value:
          totals.serviceTaxTotals.totalInBRL -
          comparison.serviceTaxTotals.totalInBRL,
      },
      {
        title: "T. Receita",
        value:
          totals.revenueTotals.totalInBRL - comparison.revenueTotals.totalInBRL,
      },
      {
        title: "T. Imposto",
        value: totals.taxes.totalInBRL - comparison.taxes.totalInBRL,
      },
      {
        title: "T. Geral",
        value: totals.totals.totalInBRL - comparison.totals.totalInBRL,
      },
    ],
    [totals, comparison],
  );

  return (
  <div
    style={{
      flexDirection: "row",
      display: "flex",
      alignItems: "center",
      width: "100%",
    }}
  >
    <div
      style={{
        flexDirection: "row",
        display: "flex",
        alignItems: "center",
        gap: 2,
        marginRight: 8,
      }}
    >
      {leadingSlot}
      <span style={{ color: "white" }}>{index + 1}</span>
    </div>
    <div
      style={{
        width: 1,
        backgroundColor: "rgba(255,255,255,0.1)",
        marginRight: 8,
        marginLeft: 8,
      }}
    />
    <div
      style={{
        display: "flex",
        alignItems: "center",
        flexDirection: "row",
        justifyContent: "space-between",
        flex: 1,
      }}
    >
      <Box
        style={{ alignItems: subtitle ? "flex-start" : "center", display: "flex" }}
        flex={0.3}
      >
        <BudgetItemIcon color="white" icon={BudgetIcons[type]} />
        <Box sx={{ ml: 0.5, minWidth: 0 }}>
          <span style={{ fontWeight: "bold", color: "white" }}>
            {BudgetItemTypeEnum[type]}
          </span>
          {subtitle}
        </Box>
      </Box>

      <Box
        display="flex"
        flex={0.7}
        justifyContent={"space-evenly"}
        gap={1}
      >
        {totalsSummary.map((item, summaryIndex) => (
          <Box
            key={item.title}
            style={{
              display: "flex",
              flexDirection: "column",
            }}
            flex={1}
          >
            <span
              style={{
                color: "white",
                fontSize: 14,
              }}
            >
              {item.title}
            </span>
            <span style={{ fontSize: 14, color: "white", fontWeight: 500 }}>
              {currencyFormatter.format(item.value.totalInBRL)}
            </span>

            {TabActive.BUDGET !== tabActive && !hideSavings && (
              <Box>
                <span
                  style={{
                    display: "fit-content",
                    fontSize: 12,
                    color:
                      savingsSummary[summaryIndex].value > 0
                        ? "#D32F2F"
                        : "#28C76F",
                    backgroundColor: "white",
                    padding: 2,
                    borderRadius: 4,
                  }}
                >
                  {currencyFormatter.format(savingsSummary[summaryIndex].value)}
                </span>
              </Box>
            )}
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          ml: 1,
          mr: 2,
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            backgroundColor: "rgba(255,255,255,0.15)",
            border: "1px solid rgba(255,255,255,0.3)",
            borderRadius: "100px",
            px: 1.5,
            py: 0.25,
            mr: 1,
            minWidth: 48,
            justifyContent: "center",
          }}
        >
          <span
            style={{
              color: "white",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.5px",
              lineHeight: 1.5,
              whiteSpace: "nowrap",
            }}
          >
            {baseValueExchangeCurrency || "BRL"}
          </span>
        </Box>
        <MoveCategorySelect
          categoryType={type}
          onMoveBillingModel={onMoveBillingModel}
          onTransferSupplier={onTransferSupplier}
          disabled={isBudgetDisabled}
        />
        <Tooltip title={`Remover ${BudgetItemTypeEnum[type]}`}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              ml: 2,
              width: 20,
              height: 20,
              borderRadius: "50%",
              backgroundColor: isBudgetDisabled ? "gray" : "#D32F2F",
              cursor: isBudgetDisabled ? "default" : "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (isBudgetDisabled) return;
              onRemoveItem(type);
            }}
          >
            <CloseIcon sx={{ color: "#FFF", fontSize: 14 }} />
          </Box>
        </Tooltip>
      </Box>
    </div>
  </div>
  );
};

export default CategoryAccordionSummary;
